import { useCallback } from 'react';

import { useMount } from 'react-use';

import { useRecoilState } from 'recoil';

import {
  useGetAuthTokenForInvokeMutation,
  useGetSharedEndpointAuthTokenForInvokeMutation,
  useGetSharedPlaygroundAuthTokenForInvokeMutation,
} from './__generated__/mutation.generated';
import { authTokenForInvokeState } from './atoms';

/**
 * Handles fetching an auth token with "invoke-model" scope. The auth token is cached
 * in state and refreshed when the `refreshAuthToken` function is called.
 * @param lazy If true, the auth token will not be fetched until the `refreshAuthToken` function is called.
 */
export default function useAuthToken(
  lazy = false,
  isSharedPlayground = false,
  isSharedEndpoint = false,
): [authToken: string | undefined, refreshAuthToken: () => Promise<string>] {
  // We want to cache the auth token for the lifetime of the page (including across SPA navigations)
  // but we don't want to cache it across page refreshes. This is to prevent the auth token
  // from being cached after the user has logged out.
  const [authToken, setAuthToken] = useRecoilState(authTokenForInvokeState);

  const [getAuthTokenForInvoke] = useGetAuthTokenForInvokeMutation();
  const [getSharedPlaygroundAuthTokenForInvoke] =
    useGetSharedPlaygroundAuthTokenForInvokeMutation();
  const [getSharedEndpointAuthTokenForInvoke] = useGetSharedEndpointAuthTokenForInvokeMutation();

  const refreshAuthToken = useCallback(async () => {
    function getAuthTokenForInvokeMutation() {
      if (isSharedEndpoint) {
        return getSharedEndpointAuthTokenForInvoke;
      }

      if (isSharedPlayground) {
        return getSharedPlaygroundAuthTokenForInvoke;
      }

      return getAuthTokenForInvoke;
    }

    const response = await getAuthTokenForInvokeMutation()();

    const { data, errors } = response;

    if (errors) {
      throw new Error('Failed to authenticate');
    }

    const { token } = data.getAuthTokenForInvoke;
    setAuthToken(token);
    return token;
  }, [
    getAuthTokenForInvoke,
    getSharedPlaygroundAuthTokenForInvoke,
    getSharedEndpointAuthTokenForInvoke,
    isSharedPlayground,
    isSharedEndpoint,
    setAuthToken,
  ]);

  useMount(() => {
    if (!lazy && !authToken) {
      refreshAuthToken();
    }
  });

  return [authToken, refreshAuthToken];
}
